import { useEffect, useRef, useState } from "react";
import { Progress } from "@/components/ui/progress";
import { Card, CardContent } from "@/components/ui/card";
import { AlertTriangle, Clock } from "lucide-react";

interface EnhancedTimerProps {
  totalMinutes: number;
  onComplete?: () => void;
  onWarning?: (minutes: number) => void;
  showProgress?: boolean;
  warningThresholds?: number[]; // minutes when to show warnings
}

export const EnhancedTimer = ({ 
  totalMinutes, 
  onComplete, 
  onWarning,
  showProgress = true,
  warningThresholds = [5, 2, 1] 
}: EnhancedTimerProps) => {
  const [remaining, setRemaining] = useState(totalMinutes * 60);
  const [warningsTriggered, setWarningsTriggered] = useState<Set<number>>(new Set());
  const intervalRef = useRef<number | null>(null);

  useEffect(() => {
    if (intervalRef.current) return;
    
    intervalRef.current = window.setInterval(() => {
      setRemaining((s) => {
        if (s <= 1) {
          if (intervalRef.current) window.clearInterval(intervalRef.current);
          onComplete?.();
          return 0;
        }

        const newRemaining = s - 1;
        const minutesLeft = Math.floor(newRemaining / 60);

        // Check for warnings
        warningThresholds.forEach(threshold => {
          if (minutesLeft === threshold && !warningsTriggered.has(threshold)) {
            setWarningsTriggered(prev => new Set([...prev, threshold]));
            onWarning?.(threshold);
          }
        });

        return newRemaining;
      });
    }, 1000);

    return () => {
      if (intervalRef.current) window.clearInterval(intervalRef.current);
    };
  }, [onComplete, onWarning, warningThresholds, warningsTriggered]);

  const totalSeconds = totalMinutes * 60;
  const elapsedSeconds = totalSeconds - remaining;
  const progressPercentage = (elapsedSeconds / totalSeconds) * 100;
  
  const minutes = Math.floor(remaining / 60);
  const seconds = remaining % 60;
  const mm = minutes.toString().padStart(2, "0");
  const ss = seconds.toString().padStart(2, "0");

  // Determine styling based on time remaining
  const isWarning = minutes <= 5;
  const isCritical = minutes <= 2;
  const isDanger = minutes < 1;

  const getTimerColor = () => {
    if (isDanger) return "text-destructive";
    if (isCritical) return "text-medical-orange";
    if (isWarning) return "text-medical-orange";
    return "text-medical-primary";
  };

  const getProgressColor = () => {
    if (isDanger) return "bg-destructive";
    if (isCritical) return "bg-medical-orange";
    if (isWarning) return "bg-medical-orange";
    return "bg-medical-primary";
  };

  const getBorderColor = () => {
    if (isDanger) return "border-destructive/50";
    if (isCritical) return "border-medical-orange/50";
    if (isWarning) return "border-medical-orange/50";
    return "border-medical-primary/20";
  };

  return (
    <Card className={`transition-medical border-2 ${getBorderColor()}`}>
      <CardContent className="p-4">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            {isWarning ? (
              <AlertTriangle className={`h-5 w-5 ${getTimerColor()} ${isDanger ? 'animate-pulse' : ''}`} />
            ) : (
              <Clock className="h-5 w-5 text-medical-primary" />
            )}
            <span className="text-sm font-medium text-muted-foreground">
              {isWarning ? "Time Warning" : "Time Remaining"}
            </span>
          </div>

          <div className={`text-2xl font-mono font-bold transition-colors ${getTimerColor()}`}>
            {mm}:{ss}
          </div>
        </div>

        {showProgress && (
          <div className="mt-3 space-y-2">
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>Progress</span>
              <span>{Math.round(progressPercentage)}%</span>
            </div>
            <div className="relative">
              <Progress 
                value={progressPercentage} 
                className="h-2"
              />
              {/* Custom progress bar color */}
              <div 
                className={`absolute top-0 left-0 h-2 rounded-full transition-all duration-300 ${getProgressColor()}`}
                style={{ width: `${progressPercentage}%` }}
              />
            </div>
          </div>
        )}

        {/* Warning messages */}
        {isWarning && (
          <div className={`mt-2 text-xs font-medium ${getTimerColor()}`}>
            {isDanger && "⚠️ Less than 1 minute remaining!"}
            {isCritical && !isDanger && "⚠️ 2 minutes remaining - hurry up!"}
            {isWarning && !isCritical && "⚠️ 5 minutes remaining"}
          </div>
        )}
      </CardContent>
    </Card>
  );
};